import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errorMessage } from '@/lib/api';
import {
  Badge,
  Button,
  Card,
  ErrorText,
  Field,
  LocalizedInput,
  Select,
  type Localized,
} from '@/components/ui';

/**
 * Шаблоны и журнал отправок. Текст пишет администратор, поэтому сервер
 * проверяет имена переменных до сохранения — об опечатке нельзя узнавать
 * из молчащего уведомления неделю спустя.
 */

const STATUS: Record<string, { label: string; tone: 'neutral' | 'green' | 'red' | 'amber' }> = {
  PENDING: { label: 'В очереди', tone: 'amber' },
  SENT: { label: 'Отправлено', tone: 'green' },
  FAILED: { label: 'Не доставлено', tone: 'red' },
  SKIPPED: { label: 'Пропущено', tone: 'neutral' },
};

interface Template {
  id: string;
  event: string;
  channel: string;
  body: Localized;
  isActive: boolean;
}

interface Catalog {
  events: string[];
  channels: string[];
  variables: string[];
}

interface LogRow {
  id: string;
  event: string;
  channel: string;
  status: string;
  destination: string | null;
  body: string;
  attempts: number;
  lastError: string | null;
  createdAt: string;
  order: { number: string } | null;
}

export default function NotificationsPage() {
  const [error, setError] = useState<string | null>(null);

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-xl font-semibold">Уведомления</h1>
        <p className="mt-1 text-sm text-neutral-500">
          Шаблоны мультиязычные, переменные вида {'{{orderNumber}}'} подставляются при отправке
        </p>
      </header>

      {error && <ErrorText>{error}</ErrorText>}

      <Templates onError={setError} />
      <Log onError={setError} />
    </div>
  );
}

function Templates({ onError }: { onError: (message: string | null) => void }) {
  const queryClient = useQueryClient();
  const [event, setEvent] = useState('');
  const [channel, setChannel] = useState('TELEGRAM');
  const [body, setBody] = useState<Localized>({});

  const catalog = useQuery({
    queryKey: ['notification-catalog'],
    queryFn: async () => (await api.get<Catalog>('/admin/notifications/catalog')).data,
  });

  const templates = useQuery({
    queryKey: ['notification-templates'],
    queryFn: async () => (await api.get<Template[]>('/admin/notifications/templates')).data,
  });

  const save = useMutation({
    mutationFn: () => api.post('/admin/notifications/templates', { event, channel, body }),
    onSuccess: () => {
      onError(null);
      setBody({});
      void queryClient.invalidateQueries({ queryKey: ['notification-templates'] });
    },
    onError: (e) => onError(errorMessage(e)),
  });

  const toggle = useMutation({
    mutationFn: (template: Template) =>
      api.post('/admin/notifications/templates', {
        event: template.event,
        channel: template.channel,
        body: template.body,
        isActive: !template.isActive,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['notification-templates'] }),
    onError: (e) => onError(errorMessage(e)),
  });

  const edit = (template: Template) => {
    setEvent(template.event);
    setChannel(template.channel);
    setBody(template.body);
  };

  return (
    <Card title="Шаблоны">
      <div className="space-y-4">
        {templates.isLoading ? (
          <p className="text-sm text-neutral-500">Загрузка…</p>
        ) : (
          <ul className="space-y-1">
            {(templates.data ?? []).map((template) => (
              <li
                key={template.id}
                className="flex flex-wrap items-center gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-neutral-50 dark:hover:bg-neutral-900"
              >
                <span className="font-mono text-xs">{template.event}</span>
                <Badge>{template.channel}</Badge>
                {!template.isActive && <Badge tone="red">выкл</Badge>}
                <span className="min-w-0 flex-1 truncate text-neutral-500">
                  {template.body.ru ?? template.body.uz ?? template.body.en}
                </span>
                <Button variant="ghost" onClick={() => edit(template)}>
                  Изм.
                </Button>
                <Button variant="ghost" onClick={() => toggle.mutate(template)}>
                  {template.isActive ? 'Выключить' : 'Включить'}
                </Button>
              </li>
            ))}
          </ul>
        )}

        <div className="space-y-3 border-t border-neutral-200 pt-4 dark:border-neutral-800">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Событие">
              <Select value={event} onChange={(e) => setEvent(e.target.value)}>
                <option value="">Выберите событие</option>
                {(catalog.data?.events ?? []).map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </Select>
            </Field>

            <Field label="Канал">
              <Select value={channel} onChange={(e) => setChannel(e.target.value)}>
                {(catalog.data?.channels ?? []).map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </Select>
            </Field>
          </div>

          <LocalizedInput label="Текст" multiline value={body} onChange={setBody} />

          <p className="text-xs text-neutral-500">
            Доступные переменные:{' '}
            <span className="font-mono">
              {(catalog.data?.variables ?? []).map((v) => `{{${v}}}`).join(' ')}
            </span>
          </p>

          <Button disabled={!event || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Сохраняем…' : 'Сохранить шаблон'}
          </Button>
        </div>
      </div>
    </Card>
  );
}

function Log({ onError }: { onError: (message: string | null) => void }) {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState('');

  const log = useQuery({
    queryKey: ['notification-log', status],
    queryFn: async () =>
      (
        await api.get<{ items: LogRow[]; total: number }>('/admin/notifications/log', {
          params: { status: status || undefined },
        })
      ).data,
  });

  const retry = useMutation({
    mutationFn: (id: string) => api.post(`/admin/notifications/log/${id}/retry`, {}),
    onSuccess: () => {
      onError(null);
      void queryClient.invalidateQueries({ queryKey: ['notification-log'] });
    },
    onError: (e) => onError(errorMessage(e)),
  });

  return (
    <Card title={`Журнал отправок${log.data ? ` · ${log.data.total}` : ''}`}>
      <Select value={status} onChange={(e) => setStatus(e.target.value)} className="mb-3 w-48">
        <option value="">Все статусы</option>
        {Object.entries(STATUS).map(([value, meta]) => (
          <option key={value} value={value}>
            {meta.label}
          </option>
        ))}
      </Select>

      {log.isLoading ? (
        <p className="text-sm text-neutral-500">Загрузка…</p>
      ) : (log.data?.items.length ?? 0) === 0 ? (
        <p className="text-sm text-neutral-500">Отправок не найдено</p>
      ) : (
        <ul className="space-y-1 text-sm">
          {log.data!.items.map((row) => (
            <li
              key={row.id}
              className="flex flex-wrap items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-neutral-50 dark:hover:bg-neutral-900"
            >
              <span className="text-xs text-neutral-500">
                {new Date(row.createdAt).toLocaleString('ru-RU')}
              </span>
              <span className="font-mono text-xs">{row.event}</span>
              <Badge>{row.channel}</Badge>
              <Badge tone={STATUS[row.status]?.tone ?? 'neutral'}>
                {STATUS[row.status]?.label ?? row.status}
              </Badge>
              {row.order && <span className="text-xs">{row.order.number}</span>}
              <span className="min-w-0 flex-1 truncate text-neutral-500">{row.body}</span>
              {row.lastError && (
                <span className="truncate text-xs text-red-500" title={row.lastError}>
                  {row.lastError}
                </span>
              )}
              {row.status === 'FAILED' && (
                <Button variant="ghost" onClick={() => retry.mutate(row.id)}>
                  Повторить
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
